import {
  checkboxChecked,
  checkboxIndeterminate,
  checkboxUnchecked,
  close,
  dropdown,
  radioChecked,
  radioUnchecked,
} from '@/components/icons/icons';
import { merge } from 'lodash';
import {
  createVuetify,
  type DefaultsInstance,
  type DisplayThresholds,
  type ThemeDefinition,
} from 'vuetify';
import { VBtn, VChip, VLabel, VRadioGroup, VVirtualScroll } from 'vuetify/components';
import { aliases, mdi } from 'vuetify/iconsets/mdi-svg';

export const DEFAULT_THEME = 'ptwLightTheme';

type Colors = NonNullable<ThemeDefinition['colors']>;

export const getColorKeyByValue = (colorValue: string, colors: Colors) => {
  const entry = Object.entries(colors).find(([, value]) => value === colorValue);
  return entry ? entry[0] : undefined;
};

export const lightThemeColors = {
  background: '#dee7ea',
  surface: '#FAFAFA',
  'on-surface': '#2b2b2b',
  'on-background': '#2b2b2b',
  'on-secondary-lighten-2': '#2b2b2b',
  // Primary
  'primary-lighten-6': '#f3f3f3',
  'primary-lighten-5': '#e4e4e4',
  'primary-lighten-4': '#d0d0d0',
  'primary-lighten-3': '#b0b0b0',
  'primary-lighten-2': '#969696',
  'primary-lighten-1': '#7c7c7c',
  primary: '#5a5a5a',
  'primary-darken-1': '#5b5b5b',
  'primary-darken-2': '#2b2b2b',
  'primary-darken-3': '#202020',
  'primary-darken-4': '#151515',
  // Secondary
  'secondary-lighten-5': '#e4e4e4',
  'secondary-lighten-4': '#d0d0d0',
  'secondary-lighten-3': '#b0b0b0',
  'secondary-lighten-2': '#969696',
  'secondary-lighten-1': '#7c7c7c',
  secondary: '#5a5a5a',
  'secondary-darken-1': '#5b5b5b',
  'secondary-darken-2': '#2b2b2b',
  'secondary-darken-3': '#202020',
  'secondary-darken-4': '#151515',
  // Error
  'error-lighten-5': '#ffa598',
  'error-lighten-4': '#ff887e',
  'error-lighten-3': '#ff6c65',
  'error-lighten-2': '#ef4f4d',
  'error-lighten-1': '#d03036',
  error: '#B00020',
  'error-darken-1': '#91000b',
  'error-darken-2': '#730000',
  'error-darken-3': '#570000',
  'error-darken-4': '#440000',
  // Info
  'info-lighten-5': '#edf7fc',
  'info-lighten-4': '#d8edf9',
  'info-lighten-3': '#b5def3',
  'info-lighten-2': '#96cce8',
  'info-lighten-1': '#83b9d5',
  info: '#6ca2be',
  'info-darken-1': '#5389a5',
  'info-darken-2': '#3a708c',
  'info-darken-3': '#235975',
  'info-darken-4': '#0f4059',
  // Success
  'success-lighten-5': '#afffd8',
  'success-lighten-4': '#92ffbc',
  'success-lighten-3': '#76e4a1',
  'success-lighten-2': '#59c886',
  'success-lighten-1': '#3aac6d',
  success: '#119154',
  'success-darken-1': '#00773c',
  'success-darken-2': '#005d26',
  'success-darken-3': '#004410',
  'success-darken-4': '#002e00',
  // Warning
  'warning-lighten-5': '#ffff9b',
  'warning-lighten-4': '#fff07f',
  'warning-lighten-3': '#ffd364',
  'warning-lighten-2': '#ffb748',
  'warning-lighten-1': '#ff9c2b',
  warning: '#F78100',
  'warning-darken-1': '#d76700',
  'warning-darken-2': '#b74d00',
  'warning-darken-3': '#993400',
  'warning-darken-4': '#7c1900',
  // starting with custom variables
  active: '#5a5a5a',
  'off-white': '#FAFAFA',
  'on-off-white': '#3a3e41',
  'state-of-use-adding': '#7fa3c0',
  'state-of-use-editing': '#7fa3c0',
  'state-of-use-enabled': '#639196',
  'state-of-use-offline': '#9a9a9a',
  'state-of-use-ready': '#25837a',
  'state-of-use-inUse': '#ff6c65',
  'state-of-use-other': '#9ba7af',
} satisfies Colors;

const ptwLightTheme: ThemeDefinition = {
  dark: false,
  colors: lightThemeColors,
};

const darkThemeColors = {
  surface: '#dee7ea',
  'on-surface': '#FAFAFA',
  'on-background': '#647d82',
  'primary-darken-2': '#639196',
  'primary-lighten-1': '#7eaaaf',
  error: '#ff6c65',
  secondary: '#b2d2d7',
} satisfies Colors;

const ptwDarkTheme = merge({}, ptwLightTheme, {
  dark: true,
  colors: darkThemeColors,
  variables: {},
});

const radioGroupDefaults = {
  color: 'secondary',
  disableRipple: true,
  trueIcon: radioChecked,
  falseIcon: radioUnchecked,
  class: 'v-radio-group-overwrite',
  hideDetails: 'auto',
};
export function getDefaults(): DefaultsInstance {
  return {
    global: {
      ripple: false,
    },
    VAppBar: {
      flat: true,
      color: 'primary',
    },
    VAutocomplete: {
      hideDetails: 'auto',
      variant: 'outlined',
      clearIcon: close,
      menuIcon: dropdown,
      autoSelectFirst: true,
      clearOnSelect: true,
    },
    VCombobox: {
      hideDetails: 'auto',
      variant: 'outlined',
      color: 'secondary',
      bgColor: 'surface',
      clearIcon: close,
      menuIcon: dropdown,
    },
    VBtn: {
      variant: 'elevated',
      class: 'text-none',
      rounded: 2,
    },
    VCard: {
      class: 'v-card-overwrite',
    },
    VCheckbox: {
      class: 'v-checkbox-overwrite',
    },
    VChip: {
      closeIcon: close,
      class: 'v-chip-overwrite',
      color: 'primary',
    },
    VAlertChip: {
      closeIcon: close,
    },
    VCheckboxBtn: {
      disableRipple: true,
      color: 'secondary',
      trueIcon: checkboxChecked,
      falseIcon: checkboxUnchecked,
      indeterminateIcon: checkboxIndeterminate,
      class: 'v-checkbox-btn-overwrite',
      hideDetails: 'auto',
    },
    VDivider: {
      color: 'primary-lighten-4',
    },
    VExpansionPanel: {
      class: 'v-expansion-panel-overwrite',
    },
    VExpansionPanelTitle: {
      class: 'v-expansion-panel-title-overwrite',
    },
    VExpansionPanelText: {
      class: 'v-expansion-panel-text-overwrite',
    },
    VInput: {
      class: 'v-input-overwrite',
      hideDetails: 'auto',
    },
    VTooltip: {
      class: 'v-tooltip-overwrite',
      transition: 'none',
      openDelay: 500,
    },
    VLabel: {
      class: 'v-label-overwrite',
    },
    VListItem: {
      class: 'v-list-item-overwrite',
    },
    VField: {
      class: 'v-field-overwrite',
    },
    VTextField: {
      variant: 'outlined',
      color: 'secondary',
      class: 'v-text-field-overwrite',
      bgColor: 'surface',
      hideDetails: 'auto',
      clearIcon: close,
    },
    VFileInput: {
      variant: 'outlined',
      color: 'secondary',
      class: 'v-text-field-overwrite',
      bgColor: 'surface',
    },
    VTextarea: {
      color: 'secondary',
      variant: 'outlined',
      bgColor: 'surface',
      class: 'v-textarea-overwrite',
      hideDetails: 'auto',
      clearIcon: close,
    },
    VDateInput: {
      variant: 'outlined',
      color: 'secondary',
      class: 'v-text-field-overwrite',
      bgColor: 'surface',
      hideDetails: 'auto',
      clearIcon: close,
    },
    VRadioGroup: {
      ...radioGroupDefaults,
    },
    SwitchVRadioGroup: {
      ...radioGroupDefaults,
      class: 'v-radio-group-overwrite v-radio-group-switch',
    },
    VSelect: {
      variant: 'outlined',
      hideDetails: 'auto',
      clearIcon: close,
      menuIcon: dropdown,
      class: 'v-select-overwrite',
      menuProps: {
        contentClass: 'text-body-1 font-weight-regular',
      },
    },
    PureButton: {
      variant: 'plain',
      style: 'padding: 0; minWidth: auto;',
      class: 'pure-button-overwrite',
    },
    VAlert: {
      density: 'compact',
      closeIcon: close,
      class: 'v-alert-overwrite',
    },
    CancelVBtn: {
      variant: 'text',
      style: 'text-transform: none;',
      class: 'cancel-v-btn-overwrite',
    },
    PrimaryVBtn: {
      class: 'primary-v-btn-overwrite',
      theme: 'ptwLightTheme',
      style: 'text-transform: none;',
    },
    SecondaryVBtn: {
      variant: 'outlined',
      class: 'secondary-v-btn-overwrite',
      style: 'text-transform: none;',
    },
    SpecialVBtn: {
      class: 'special-v-btn-overwrite',
      style: 'text-transform: none;',
    },
    TextVBtn: {
      variant: 'text',
      class: 'text-v-btn-overwrite',
      style: 'text-transform: none;',
    },
    GhostVBtn: {
      variant: 'text',
      class: 'ghost-v-btn-overwrite',
      style: 'text-transform: none;',
    },
    VContainer: {
      fluid: true,
    },
    VTab: {
      VBtn: {
        class: 'text-body-2',
      },
    },
    VSheet: {
      rounded: true,
      color: 'off-white',
    },
    VMenu: {
      class: 'v-menu-overwrite',
    },
    VVirtualScroll: {
      class: 'v-virtual-scroll-overwrite',
    },
  };
}

const mobileBreakpoint = 'lg';
const displayThresholds: DisplayThresholds = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
  xxl: 2560,
};

export const vuetify = createVuetify({
  display: {
    mobileBreakpoint,
    thresholds: displayThresholds,
  },
  aliases: {
    PureButton: VBtn,
    CancelVBtn: VBtn,
    VAlertChip: VChip,
    InputFieldVLabel: VLabel,
    PrimaryVBtn: VBtn,
    SecondaryVBtn: VBtn,
    SpecialVBtn: VBtn,
    TextVBtn: VBtn,
    GhostVBtn: VBtn,
    SwitchVRadioGroup: VRadioGroup,
    InsetVVirtualScroll: VVirtualScroll,
  },
  defaults: getDefaults(),
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: {
      mdi,
    },
  },
  locale: {
    adapter: undefined, // unfortunately no adapter available yet for i18next: https://github.com/vuetifyjs/vuetify/issues/14817
  },
  theme: {
    defaultTheme: DEFAULT_THEME,
    themes: { ptwLightTheme, ptwDarkTheme },
  },
});
